import { EventType } from "@/analytics/analytics-events";
import { UploadImageProperties } from "@/analytics/generic-events-properties";
import {
  ElementFileUploadContext,
  useElementFileUploadContext,
} from "@/components/common/point-cloud-file-upload-context/element-file-upload-context";
import { ElementIconType, TYPE_TO_ICON_MAP } from "@/components/ui/icons";
import { RootState } from "@/store/store";
import { useAppStore } from "@/store/store-hooks";
import { pickImageFiles } from "@/utils/pick-file";
import { FaroMenuItem, PlusIcon } from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { assert } from "@faro-lotv/foundation";
import {
  GUID,
  isIElementImg2d,
  isIElementRoomSection,
  isIElementTimeseries,
} from "@faro-lotv/ielement-types";
import { selectAncestor, selectIElement } from "@faro-lotv/project-source";
import { useApiClientContext } from "@faro-lotv/service-wires";
import type { JSX } from "react";
import {
  ContextMenuAction,
  ContextMenuActionType,
  NestedMenuHandlerFnArgs,
  NestedMenuObject,
} from "../action-types";
import { ContextMenuItemLabel } from "../context-menu-action-item";

// Note: The values in this enum are shown as labels in the UI.
export enum ImageType {
  /** Indicating a panorama image */
  Img360 = "360 photo",
  /** Indicating a 2D image */
  Img2D = "2D photo",
  /** Indicating that the image type should be automatically determined */
  auto = "photo",
}

/**
 * Opens a file picker to upload a 360° image and uploads it for the specified section room.
 *
 * @param elementId - The ID of the target element selected in the context menu.
 * @param elementFileUploadContext - Context providing the upload handler function.
 * @param state - store state
 * @param imgType - the type of image to be uploaded
 */
async function uploadImages(
  elementId: GUID,
  elementFileUploadContext: ElementFileUploadContext,
  state: RootState,
  imgType: ImageType,
): Promise<void> {
  const files = await pickImageFiles();

  let sectionRoomId = elementId;
  const targetElement = selectIElement(elementId)(state);
  assert(targetElement, "Target menu element must exist");

  if (isIElementImg2d(targetElement)) {
    const roomSection = selectAncestor(
      targetElement,
      isIElementRoomSection,
    )(state);
    assert(roomSection, "Room section must exist");
    sectionRoomId = roomSection.id;
  } else {
    const timeSeries = selectAncestor(
      targetElement,
      isIElementTimeseries,
    )(state);

    if (timeSeries) {
      const roomSection = selectAncestor(
        timeSeries,
        isIElementRoomSection,
      )(state);
      assert(roomSection, "Room section must exist");
      sectionRoomId = roomSection.id;
    }
  }

  Analytics.track<UploadImageProperties>(
    imgType === ImageType.Img360
      ? EventType.upload360Photo
      : EventType.upload2DPhoto,
    {
      numberOfImages: files.length,
      isNewWaypoint: false,
    },
  );

  // Call upload function
  elementFileUploadContext.uploadMultiImageDataFiles(
    files,
    sectionRoomId,
    imgType,
  );
}

const nestedMenuProps: NestedMenuObject = {
  options: [
    {
      id: "360-photos",
      label: "360° photos",
      icon: ElementIconType.PanoramaIcon,
      onClick: ({
        menuItemId,
        elementFileUploadContext,
        state,
      }: NestedMenuHandlerFnArgs) =>
        uploadImages(
          menuItemId,
          elementFileUploadContext,
          state,
          ImageType.Img360,
        ),
    },
    {
      id: "2d-photos",
      label: "2D photos",
      icon: ElementIconType.ImageIcon,
      onClick: ({
        menuItemId,
        elementFileUploadContext,
        state,
      }: NestedMenuHandlerFnArgs) =>
        uploadImages(
          menuItemId,
          elementFileUploadContext,
          state,
          ImageType.Img2D,
        ),
    },
  ],
};

type AddDataActionProps = {
  /** The current element ID */
  elementId: GUID;
  /** Enable the dark version of the menu */
  dark?: boolean;
  /** Callback to execute when the context menu should be closed */
  closeContextMenu?(): void;
};

function AddDataContent({
  elementId,
  dark,
  closeContextMenu,
}: AddDataActionProps): JSX.Element {
  const state = useAppStore().getState();
  const { projectApiClient: projectApi } = useApiClientContext();
  const elementFileUploadContext = useElementFileUploadContext();

  return (
    <FaroMenuItem
      label="Add data"
      aria-label="Add data"
      Icon={PlusIcon}
      dark={dark}
    >
      {nestedMenuProps.options.map(({ id, label, icon, onClick }, index) => (
        <FaroMenuItem
          dark={dark}
          Icon={icon ? TYPE_TO_ICON_MAP[icon] : undefined}
          onClick={() => {
            onClick({
              nestedMenuItemId: id,
              menuItemId: elementId,
              state,
              projectApi,
              elementFileUploadContext,
            });
            closeContextMenu?.();
          }}
          key={index}
          sx={{
            display: "flex",
            alignItems: "center",
          }}
          label={<ContextMenuItemLabel label={label} />}
          aria-label={id}
        />
      ))}
    </FaroMenuItem>
  );
}

/**
 * @returns A context menu action component to add data (images) to an element.
 */
export function AddDataAction({
  elementId,
  dark,
  closeContextMenu,
}: AddDataActionProps): JSX.Element {
  return (
    <AddDataContent
      elementId={elementId}
      dark={dark}
      closeContextMenu={closeContextMenu}
    />
  );
}

export const ADD_DATA_ACTION: ContextMenuAction = {
  type: ContextMenuActionType.addData,
  CustomComponent: AddDataAction,
};
